import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Drawer } from '../ui/Drawer';
import { SelectField, deletedRefProps } from './fields';
import DateField from './DateField';
import EditHistoryLink from './EditHistoryLink';
import type { CustomDrawerProps } from './TablemaintPage';
import { titleCase } from '../../utils/titleCase';
import { locationOptionLabel } from '../LocationTypeAhead';
import { formatCurrency } from '../../utils/money';
import tablemaintService, { TablemaintRow } from '../../services/tablemaint.service';
import {
  CustomChassisChargesOptions, ScacLine, getCustomChassisChargesOptions,
  listScacLines, saveScacLines,
} from '../../services/customchassischarges.service';

/**
 * Bespoke edit/create drawer for Custom Chassis Charges (cms.customchassischarges
 * + a customchassischarges_scac SCAC-line subtable).
 *
 * Mirrors V1 editcustomchassischarges: Bill To / Customer / Delivery Location
 * enriched typeaheads (same options as Container Status Reports) + a Region
 * dropdown, then an inline SCAC-lines editor (SCAC, Chassis Pool, Addl Cost,
 * Invoiced, Free Days, Type, Expiration). Free Days + Type are editable only when
 * the line is Invoiced. Save rules: at least one of Bill To/Customer/Location/Broker;
 * one entry per SCAC/Chassis Pool; Free Days Type required when Free Days set;
 * Expiration required. Only non-deleted SCAC lines are shown; removed lines are
 * soft-deleted.
 */
const TABLE = 'customchassischarges';

const s = (v: any): string => (v == null ? '' : String(v));
const fkSeed = (v: any): string => (v && String(v) !== '0' ? String(v) : '');
const fkOut = (v: any): string => (s(v).trim() || '0');

const ADDR = (o: { address?: string; city?: string; state?: string; recno: number }): string => {
  const line = [titleCase(s(o.address)), titleCase(s(o.city)), s(o.state)].filter(Boolean).join(', ');
  return line;
};

const YN_OPTS = [{ value: 'N', label: 'No' }, { value: 'Y', label: 'Yes' }];

const blankLine = (): ScacLine => ({
  recno: 0, scac: '', chassispool: '', createaddlcost: 'Y', invoiced: 'Y',
  numfreedays: '', daystype: '', expirationdate: '',
});

const CustomChassisChargesEditDrawer: React.FC<CustomDrawerProps> = ({ row, onClose, onSaved }) => {
  const isNew = !row;
  const recno = row ? Number(row.recno) : 0;

  const [record, setRecord] = useState<TablemaintRow>(row ?? {});
  const [opts, setOpts] = useState<CustomChassisChargesOptions | null>(null);
  const [form, setForm] = useState<TablemaintRow>({ billto: '', customercode: '', locationcode: '', brokercode: '', region: '' });
  const [formDeleted, setFormDeleted] = useState(false);
  const [lines, setLines] = useState<ScacLine[]>([]);
  const [deletes, setDeletes] = useState<number[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getCustomChassisChargesOptions().then(setOpts).catch(() => {});
    if (!isNew) {
      tablemaintService.get(TABLE, recno).then(setRecord).catch(() => {});
      listScacLines(recno).then(setLines).catch(() => {});
    }
  }, [isNew, recno]);

  useEffect(() => {
    setForm({
      brokercode: fkSeed(record.brokercode),
      billto: fkSeed(record.billto),
      customercode: fkSeed(record.customercode),
      locationcode: fkSeed(record.locationcode),
      region: s(record.region),
    });
    setFormDeleted(s(record.deleted) === 'Y');
  }, [record]);

  useEffect(() => {
    if (saveError || showErrors) topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [saveError, showErrors]);

  const setField = (k: string, v: any) => setForm((p) => ({ ...p, [k]: v }));

  const setLine = (idx: number, key: keyof ScacLine, value: string) => {
    setLines((prev) => prev.map((ln, i) => {
      if (i !== idx) return ln;
      const next = { ...ln, [key]: value };
      // Free Days + Type are locked (and cleared) when the line isn't Invoiced.
      if (key === 'invoiced' && value !== 'Y') { next.numfreedays = ''; next.daystype = ''; }
      return next;
    }));
  };
  const addLine = () => setLines((prev) => [...prev, blankLine()]);
  const removeLine = (idx: number) => {
    setLines((prev) => {
      const ln = prev[idx];
      if (ln.recno) setDeletes((d) => [...d, ln.recno]);
      return prev.filter((_, i) => i !== idx);
    });
  };

  // Enriched typeahead options (same shapes as Container Status Reports).
  const billtoOpts = useMemo(() => (opts?.billtos ?? []).map((b) => ({
    value: String(b.recno), label: titleCase(s(b.cname)), labelSuffix: `#${b.recno}`, info: ADDR(b),
    description: `${ADDR(b)}  ·  Sage ${s(b.mas90code) || '—'}  ·  AR ${formatCurrency(b.arbalance)}`,
  })), [opts]);
  const customerOpts = useMemo(() => (opts?.customers ?? []).map((c) => ({
    value: String(c.recno), label: titleCase(s(c.custname)), labelSuffix: `#${c.recno}`, description: `${ADDR(c)}  ·  ${s(c.custtype)}`,
  })), [opts]);
  const locationOpts = useMemo(() => (opts?.locations ?? []).map((l) => ({
    value: String(l.recno), label: locationOptionLabel(titleCase(s(l.lname)), s(l.cyalias)), labelSuffix: `#${l.recno}`, description: ADDR(l),
  })), [opts]);
  const brokerOpts = useMemo(() => (opts?.brokers ?? []).map((b) => ({
    value: String(b.recno), label: titleCase(s(b.name)), labelSuffix: `#${b.recno}`, description: ADDR(b),
  })), [opts]);
  const regionOpts = useMemo(() => (opts?.regions ?? []).map((r) => ({ value: r, label: r || '— None —' })), [opts]);

  // Inline (native-select) line options.
  const scacCellOpts = useMemo(() => [{ value: '', label: '—' }, ...(opts?.scacs ?? []).map((x) => ({
    value: x.scac, label: x.name ? `${x.scac} — ${titleCase(x.name)}` : x.scac,
  }))], [opts]);
  const poolCellOpts = useMemo(() => [{ value: '', label: '—' }, ...(opts?.chassispools ?? []).map((p) => ({ value: p, label: p }))], [opts]);
  const daystypeOpts = useMemo(() => (opts?.daystypes ?? ['', 'Business', 'Calendar']).map((d) => ({ value: d, label: d || '—' })), [opts]);

  const errors = useMemo(() => {
    const errs: string[] = [];
    if (!fkSeed(form.billto) && !fkSeed(form.customercode) && !fkSeed(form.locationcode) && !fkSeed(form.brokercode)) {
      errs.push('Enter at least one of Bill To, Customer, Delivery Location, or Broker.');
    }
    const seen = new Set<string>();
    lines.forEach((ln) => {
      const scac = s(ln.scac).trim();
      if (!scac) return;
      const key = `${scac}~${s(ln.chassispool).trim()}`;
      if (seen.has(key)) errs.push(`There can only be 1 entry per SCAC/Chassis Pool (${scac}).`);
      seen.add(key);
      if (!s(ln.expirationdate).trim()) errs.push(`Expiration Date is missing for ${scac}.`);
    });
    return errs;
  }, [form, lines]);
  const banner = [...(showErrors ? errors : []), ...(saveError ? [saveError] : [])];

  const handleSave = useCallback(async () => {
    if (errors.length) { setShowErrors(true); return; }
    setShowErrors(false);
    setSaveError(null);
    setIsSaving(true);
    const outLines = lines.filter((l) => s(l.scac).trim());
    try {
      let mainRecno = recno;
      if (isNew) {
        const res = await tablemaintService.create(TABLE, {
          billto: fkOut(form.billto), customercode: fkOut(form.customercode),
          locationcode: fkOut(form.locationcode), brokercode: fkOut(form.brokercode), region: s(form.region),
        });
        mainRecno = res.recno;
      } else {
        const patch: TablemaintRow = {};
        (['billto', 'customercode', 'locationcode', 'brokercode'] as const).forEach((k) => {
          if (fkOut(form[k]) !== fkOut(record[k])) patch[k] = fkOut(form[k]);
        });
        if (s(form.region) !== s(record.region)) patch.region = s(form.region);
        if (formDeleted !== (s(record.deleted) === 'Y')) patch.deleted = formDeleted ? 'Y' : 'N';
        if (Object.keys(patch).length) await tablemaintService.update(TABLE, recno, patch);
      }
      await saveScacLines(mainRecno, outLines, isNew ? [] : deletes);
      onSaved();
      onClose();
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || err?.message || 'Failed to save custom chassis charge.');
    } finally {
      setIsSaving(false);
    }
  }, [errors, isNew, form, record, formDeleted, lines, deletes, recno, onClose, onSaved]);

  const cellCls = 'w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white disabled:opacity-50 disabled:bg-gray-100 dark:disabled:bg-gray-800';
  const cellSelect = (value: string, onChange: (v: string) => void, options: { value: string; label: string }[], disabled?: boolean) => (
    <select value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} className={cellCls}>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <button onClick={onClose} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
        Cancel
      </button>
      <button onClick={handleSave} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2">
        {isSaving ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>)
          : (<><CheckIcon className="w-4 h-4" /> {isNew ? 'Create Charge' : 'Save Changes'}</>)}
      </button>
    </div>
  );

  const title = isNew ? 'Add Custom Chassis Charge' : `Edit Custom Chassis Charge (#${recno})`;

  return (
    <Drawer isOpen onClose={onClose} title={title} position="right" width="72rem" footer={footer}>
      <div className="space-y-6">
        <div ref={topRef} />
        {!isNew && (
          <div className="flex justify-end">
            <EditHistoryLink table={TABLE} recno={recno} />
          </div>
        )}
        {banner.length > 0 && (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3">
            <ul className="list-disc list-inside text-sm text-red-800 dark:text-red-300">
              {banner.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          </div>
        )}

        {/* Applies to — at least one required. */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <legend className="px-2 text-sm font-semibold text-label">
            Applies To <span className="text-xs font-normal text-gray-400">(at least one of Bill To / Customer / Location / Broker)</span>
          </legend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SelectField label="Bill To" value={s(form.billto)} onChange={(v) => setField('billto', v)}
              options={billtoOpts} disabled={isSaving} showDescriptions showSelectedInfo
              {...deletedRefProps('Bill To', record.cname, form.billto, record.billtodeleted)} />
            <SelectField label="Customer" value={s(form.customercode)} onChange={(v) => setField('customercode', v)}
              options={customerOpts} disabled={isSaving} showDescriptions showSelectedInfo
              {...deletedRefProps('Customer', record.custname, form.customercode, record.custdeleted)} />
            <SelectField label="Delivery Location" value={s(form.locationcode)} onChange={(v) => setField('locationcode', v)}
              options={locationOpts} disabled={isSaving} showDescriptions showSelectedInfo
              {...deletedRefProps('Delivery Location', record.lname, form.locationcode, record.locdeleted)} />
            <SelectField label="Broker" value={s(form.brokercode)} onChange={(v) => setField('brokercode', v)}
              options={brokerOpts} disabled={isSaving} showDescriptions showSelectedInfo
              {...deletedRefProps('Broker', record.brokername, form.brokercode, record.brokerdeleted)} />
            <SelectField label="Region" value={s(form.region)} onChange={(v) => setField('region', v)}
              options={regionOpts} disabled={isSaving} />
          </div>
        </fieldset>

        {/* SCAC lines. */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <legend className="px-2 text-sm font-semibold text-label">SCAC Details</legend>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="text-left text-muted">
                  <th className="py-1 pr-2 font-medium">SCAC</th>
                  <th className="py-1 px-2 font-medium">Chassis Pool</th>
                  <th className="py-1 px-2 font-medium">Addl Cost</th>
                  <th className="py-1 px-2 font-medium">Invoiced</th>
                  <th className="py-1 px-2 font-medium">Free Days</th>
                  <th className="py-1 px-2 font-medium">Type</th>
                  <th className="py-1 px-2 font-medium">Expiration</th>
                  <th className="py-1 pl-2" />
                </tr>
              </thead>
              <tbody>
                {lines.length === 0 ? (
                  <tr><td colSpan={8} className="py-2 text-gray-400 dark:text-gray-500">No SCAC lines yet.</td></tr>
                ) : lines.map((ln, idx) => {
                  const invoicedOn = s(ln.invoiced) === 'Y';
                  return (
                    <tr key={idx} className="border-t border-gray-100 dark:border-gray-700/60 align-top">
                      <td className="py-1.5 pr-2 w-[22%]">{cellSelect(s(ln.scac), (v) => setLine(idx, 'scac', v), scacCellOpts, isSaving)}</td>
                      <td className="py-1.5 px-2 w-[14%]">{cellSelect(s(ln.chassispool), (v) => setLine(idx, 'chassispool', v), poolCellOpts, isSaving)}</td>
                      <td className="py-1.5 px-2 w-[9%]">{cellSelect(s(ln.createaddlcost), (v) => setLine(idx, 'createaddlcost', v), YN_OPTS, isSaving)}</td>
                      <td className="py-1.5 px-2 w-[9%]">{cellSelect(s(ln.invoiced), (v) => setLine(idx, 'invoiced', v), YN_OPTS, isSaving)}</td>
                      <td className="py-1.5 px-2 w-[10%]">
                        <input type="number" value={s(ln.numfreedays)} disabled={isSaving || !invoicedOn}
                          onChange={(e) => setLine(idx, 'numfreedays', e.target.value)} className={cellCls} />
                      </td>
                      <td className="py-1.5 px-2 w-[13%]">{cellSelect(s(ln.daystype), (v) => setLine(idx, 'daystype', v), daystypeOpts, isSaving || !invoicedOn)}</td>
                      <td className="py-1.5 px-2 w-[17%]">
                        <DateField value={s(ln.expirationdate)} onChange={(v) => setLine(idx, 'expirationdate', v)} disabled={isSaving} />
                      </td>
                      <td className="py-1.5 pl-2 text-right">
                        <button type="button" onClick={() => removeLine(idx)} disabled={isSaving}
                          className="p-1 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300" title="Remove line">
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <button type="button" onClick={addLine} disabled={isSaving}
            className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
            <PlusIcon className="w-4 h-4" /> Add SCAC line
          </button>
        </fieldset>

        {/* Soft-delete (edit only). */}
        {!isNew && (
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
            <label className="flex items-start gap-3 cursor-pointer select-none py-1">
              <input type="checkbox" checked={formDeleted} disabled={isSaving}
                onChange={(e) => setFormDeleted(e.target.checked)}
                className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
              <span className="text-sm font-medium text-label">Deleted</span>
            </label>
            <p className="mt-1 ml-7 text-xs text-muted">
              Soft-deletes this charge — hidden from filters but preserved in history. Uncheck and save to restore.
            </p>
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default CustomChassisChargesEditDrawer;
